import { Outlet, Link, createRootRoute, useRouter } from "@tanstack/react-router";

import { DemoModeProvider } from "@/hooks/use-demo-mode";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ThemeProvider } from "@/hooks/use-theme";
import { Toaster } from "@/components/ui/sonner";
import { currentBrand } from "@/lib/brand";
import { UserMenu } from "@/components/layout/UserMenu";
import { Home, HelpCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CookieConsentProvider } from "@/hooks/use-cookie-consent";
import { CookieBanner, CookieSettingsDialog } from "@/components/CookieConsent";

function NotFoundComponent() {
  const { profile, loading } = useAuth();
  const isStaff = profile?.role === "staff";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 mx-auto flex max-w-6xl items-center justify-between px-6 py-4 bg-background/80 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2">
          <img
            src={currentBrand.logoSrc}
            alt={`${currentBrand.name} logo`}
            className="h-10 w-auto"
          />
          <span className="font-bold">{currentBrand.name}</span>
        </div>
        {!loading && profile ? (
          <UserMenu />
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full">
                Sign in to FlightDeck
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
                Create a trade account
              </Button>
            </Link>
          </div>
        )}
      </header>

      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-7xl font-bold text-foreground">404</h1>
          <h2 className="mt-4 text-2xl font-semibold text-foreground">Page not found</h2>
          <p className="mt-4 text-muted-foreground">
            The page you're looking for doesn't exist or has been moved. Please check the URL or
            navigate to one of the sections below.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/">
              <Button variant="default" className="rounded-full">
                <Home className="mr-2 h-4 w-4" /> Home
              </Button>
            </Link>
            <Link to="/help">
              <Button variant="outline" className="rounded-full">
                <HelpCircle className="mr-2 h-4 w-4" /> Help Center
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="rounded-full">
                <FileText className="mr-2 h-4 w-4" /> Pricing & artwork guidelines
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/50 bg-sky-50 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img
                  src={currentBrand.logoSrc}
                  alt={`${currentBrand.name} logo`}
                  className="h-10 w-auto"
                />
                <span className="font-bold text-slate-800 text-lg">FlightDeck</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                FlightDeck is a trade portal by Resolute and CraftyJet.
              </p>
              <p className="text-xs text-slate-500">
                © {new Date().getFullYear()} Resolute DTG Ltd. All rights reserved.
              </p>
            </div>
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-slate-800">Quick links</h3>
              <ul className="space-y-4 text-sm">
                <li>
                  <Link to="/pricing" className="text-primary hover:underline py-1 inline-block">
                    Pricing & artwork
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-primary hover:underline py-1 inline-block">
                    Customer sign in
                  </Link>
                </li>
                <li>
                  <Link
                    to="/staff-login"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    Staff login
                  </Link>
                </li>
                <li>
                  <a
                    href="mailto:support@resoluteink.co.uk?subject=Request FlightDeck access"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    Request access
                  </a>
                </li>
                <li>
                  <a href="#" className="text-primary hover:underline py-1 inline-block">
                    Privacy & terms
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  // Log that __root has loaded to confirm app bootstrap
  if (typeof window !== "undefined") {
    console.log("[FlightDeck] __root loaded", new Date().toISOString());
  }

  // Add global error handlers to capture invariant failures with full context
  if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
      console.error("[FlightDeck] Global error:", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        errorMessage: event.error?.message,
        errorStack: event.error?.stack,
        location: window.location.href,
        userAgent: navigator.userAgent,
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      console.error("[FlightDeck] Unhandled rejection:", {
        reason: event.reason,
        reasonMessage: event.reason instanceof Error ? event.reason.message : String(event.reason),
        reasonStack: event.reason instanceof Error ? event.reason.stack : undefined,
        location: window.location.href,
        userAgent: navigator.userAgent,
      });
    });
  }

  return (
    <DemoModeProvider>
      <ThemeProvider>
        <AuthProvider>
          <CookieConsentProvider>
            <a href="#main-content" className="skip-link">
              Skip to main content
            </a>
            <Outlet />
            <Toaster richColors position="top-right" />
            <CookieBanner />
            <CookieSettingsDialog />
          </CookieConsentProvider>
        </AuthProvider>
      </ThemeProvider>
    </DemoModeProvider>
  );
}
